import { useState, useRef, useCallback } from 'react';

const COLORS = {
  frame: '#475569',
  frameLight: '#64748B',
  glass: '#DBEAFE',
  glassBorder: '#93C5FD',
  handle: '#334155',
  divider: '#94A3B8',
  accent: '#0052FF',
  bg: '#F1F5F9',
  dimension: '#0052FF',
  gridLine: '#E2E8F0'
};

export default function WindowBuilder({ category, style, material, width, height, onResize }) {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [hoverHandle, setHoverHandle] = useState(null);

  const svgW = 400;
  const svgH = 360;
  const padding = 50;
  const maxDrawW = svgW - padding * 2;
  const maxDrawH = svgH - padding * 2;

  const aspect = width / height;
  let drawW, drawH;
  if (aspect > maxDrawW / maxDrawH) {
    drawW = maxDrawW;
    drawH = maxDrawW / aspect;
  } else {
    drawH = maxDrawH;
    drawW = maxDrawH * aspect;
  }

  const x0 = (svgW - drawW) / 2;
  const y0 = (svgH - drawH) / 2;
  const frameT = Math.max(6, drawW * 0.04);

  const handleMouseDown = useCallback((axis, e) => {
    e.preventDefault();
    setDragging(axis);
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = width;
    const startH = height;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const scale = 10 / Math.max(drawW, drawH);
      if (axis === 'x' || axis === 'both') {
        const newW = Math.max(1, Math.round((startW + dx * scale) * 2) / 2);
        onResize?.({ width: newW, height: axis === 'both' ? Math.max(1, Math.round((startH + dy * scale) * 2) / 2) : height });
      }
      if (axis === 'y' && axis !== 'both') {
        const newH = Math.max(1, Math.round((startH + dy * scale) * 2) / 2);
        onResize?.({ width, height: newH });
      }
    };
    const onUp = () => {
      setDragging(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [width, height, drawW, drawH, onResize]);

  const renderGrid = () => (
    <g opacity="0.3">
      {Array.from({ length: Math.floor(svgW / 20) + 1 }, (_, i) => (
        <line key={`gv${i}`} x1={i * 20} y1={0} x2={i * 20} y2={svgH} stroke={COLORS.gridLine} strokeWidth={0.5} />
      ))}
      {Array.from({ length: Math.floor(svgH / 20) + 1 }, (_, i) => (
        <line key={`gh${i}`} x1={0} y1={i * 20} x2={svgW} y2={i * 20} stroke={COLORS.gridLine} strokeWidth={0.5} />
      ))}
    </g>
  );

  const renderDimensions = () => (
    <g>
      {/* Width dimension */}
      <line x1={x0} y1={y0 + drawH + 20} x2={x0 + drawW} y2={y0 + drawH + 20} stroke={COLORS.dimension} strokeWidth={1.5} markerStart="url(#arrowL)" markerEnd="url(#arrowR)" />
      <text x={x0 + drawW / 2} y={y0 + drawH + 38} fill={COLORS.dimension} fontSize="13" fontFamily="monospace" textAnchor="middle" fontWeight="600">
        {width} ft
      </text>
      {/* Height dimension */}
      <line x1={x0 - 20} y1={y0} x2={x0 - 20} y2={y0 + drawH} stroke={COLORS.dimension} strokeWidth={1.5} markerStart="url(#arrowU)" markerEnd="url(#arrowD)" />
      <text x={x0 - 28} y={y0 + drawH / 2} fill={COLORS.dimension} fontSize="13" fontFamily="monospace" textAnchor="middle" fontWeight="600" transform={`rotate(-90, ${x0 - 28}, ${y0 + drawH / 2})`}>
        {height} ft
      </text>
    </g>
  );

  const renderHandle = (cx, cy, axis) => {
    const isHover = hoverHandle === axis;
    const isDrag = dragging === axis;
    return (
      <g
        onMouseDown={(e) => handleMouseDown(axis, e)}
        onMouseEnter={() => setHoverHandle(axis)}
        onMouseLeave={() => setHoverHandle(null)}
        style={{ cursor: axis === 'x' ? 'ew-resize' : axis === 'y' ? 'ns-resize' : 'nwse-resize' }}
      >
        <circle cx={cx} cy={cy} r={isHover || isDrag ? 8 : 6} fill={isDrag ? COLORS.accent : isHover ? '#3B82F6' : 'white'} stroke={COLORS.accent} strokeWidth={2} />
        <circle cx={cx} cy={cy} r={3} fill={isDrag || isHover ? 'white' : COLORS.accent} />
      </g>
    );
  };

  const renderWindow = () => {
    const ix = x0 + frameT;
    const iy = y0 + frameT;
    const iw = drawW - frameT * 2;
    const ih = drawH - frameT * 2;

    if (style === 'casement') {
      return (
        <g>
          <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
          <rect x={ix} y={iy} width={iw / 2 - 2} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          <rect x={ix + iw / 2 + 2} y={iy} width={iw / 2 - 2} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          <line x1={ix + iw / 2 - 2} y1={iy + ih * 0.3} x2={ix + iw / 2 - 2} y2={iy + ih * 0.7} stroke={COLORS.handle} strokeWidth={3} strokeLinecap="round" />
          <line x1={ix + iw / 2 + 2} y1={iy + ih * 0.3} x2={ix + iw / 2 + 2} y2={iy + ih * 0.7} stroke={COLORS.handle} strokeWidth={3} strokeLinecap="round" />
          {/* Hinge marks */}
          <circle cx={ix + 4} cy={iy + ih * 0.25} r={2} fill={COLORS.frameLight} />
          <circle cx={ix + 4} cy={iy + ih * 0.75} r={2} fill={COLORS.frameLight} />
          <circle cx={ix + iw - 4} cy={iy + ih * 0.25} r={2} fill={COLORS.frameLight} />
          <circle cx={ix + iw - 4} cy={iy + ih * 0.75} r={2} fill={COLORS.frameLight} />
        </g>
      );
    }

    if (style === 'double-hung') {
      return (
        <g>
          <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
          <rect x={ix} y={iy} width={iw} height={ih / 2 - 2} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          <rect x={ix} y={iy + ih / 2 + 2} width={iw} height={ih / 2 - 2} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          <line x1={ix} y1={iy + ih / 2} x2={ix + iw} y2={iy + ih / 2} stroke={COLORS.frame} strokeWidth={4} />
          <rect x={ix + iw / 2 - 8} y={iy + ih / 2 - 3} width={16} height={6} rx={3} fill={COLORS.handle} />
          {/* Cross bars */}
          <line x1={ix + iw / 2} y1={iy} x2={ix + iw / 2} y2={iy + ih / 2 - 2} stroke={COLORS.divider} strokeWidth={1} opacity={0.5} />
          <line x1={ix + iw / 2} y1={iy + ih / 2 + 2} x2={ix + iw / 2} y2={iy + ih} stroke={COLORS.divider} strokeWidth={1} opacity={0.5} />
        </g>
      );
    }

    // Default / sliding window
    return (
      <g>
        <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
        <rect x={ix} y={iy} width={iw * 0.52} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
        <rect x={ix + iw * 0.48} y={iy} width={iw * 0.52} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} opacity={0.85} />
        <rect x={ix + iw * 0.48 + 4} y={iy + ih / 2 - 4} width={12} height={8} rx={2} fill={COLORS.handle} />
        {/* Track lines */}
        <line x1={ix} y1={y0 + drawH - frameT + 2} x2={ix + iw} y2={y0 + drawH - frameT + 2} stroke={COLORS.frameLight} strokeWidth={1} />
      </g>
    );
  };

  const renderDoor = () => {
    const ix = x0 + frameT;
    const iy = y0 + frameT;
    const iw = drawW - frameT * 2;
    const ih = drawH - frameT * 2;

    if (style === 'french') {
      const panelW = iw / 2 - 3;
      return (
        <g>
          <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
          {/* Left door */}
          <rect x={ix} y={iy} width={panelW} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          {/* Right door */}
          <rect x={ix + panelW + 6} y={iy} width={panelW} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
          {/* Glass panes grid left */}
          <line x1={ix} y1={iy + ih * 0.33} x2={ix + panelW} y2={iy + ih * 0.33} stroke={COLORS.divider} strokeWidth={1.5} />
          <line x1={ix} y1={iy + ih * 0.66} x2={ix + panelW} y2={iy + ih * 0.66} stroke={COLORS.divider} strokeWidth={1.5} />
          {/* Glass panes grid right */}
          <line x1={ix + panelW + 6} y1={iy + ih * 0.33} x2={ix + iw} y2={iy + ih * 0.33} stroke={COLORS.divider} strokeWidth={1.5} />
          <line x1={ix + panelW + 6} y1={iy + ih * 0.66} x2={ix + iw} y2={iy + ih * 0.66} stroke={COLORS.divider} strokeWidth={1.5} />
          {/* Handles */}
          <circle cx={ix + panelW - 8} cy={iy + ih * 0.5} r={4} fill={COLORS.handle} />
          <circle cx={ix + panelW + 14} cy={iy + ih * 0.5} r={4} fill={COLORS.handle} />
          {/* Center divider */}
          <rect x={ix + panelW} y={y0} width={6} height={drawH} fill={COLORS.frame} />
        </g>
      );
    }

    // Sliding door
    return (
      <g>
        <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
        <rect x={ix} y={iy} width={iw * 0.52} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
        <rect x={ix + iw * 0.48} y={iy} width={iw * 0.52} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} opacity={0.85} />
        {/* Handle bar */}
        <rect x={ix + iw * 0.48 + 6} y={iy + ih * 0.4} width={4} height={ih * 0.2} rx={2} fill={COLORS.handle} />
        {/* Bottom track */}
        <line x1={x0} y1={y0 + drawH - 2} x2={x0 + drawW} y2={y0 + drawH - 2} stroke={COLORS.frameLight} strokeWidth={3} />
        <line x1={x0} y1={y0 + drawH} x2={x0 + drawW} y2={y0 + drawH} stroke={COLORS.frame} strokeWidth={2} />
      </g>
    );
  };

  const renderPartition = () => {
    const ix = x0 + frameT;
    const iy = y0 + frameT;
    const iw = drawW - frameT * 2;
    const ih = drawH - frameT * 2;
    const panels = Math.max(2, Math.round(width / 2));
    const panelW = iw / panels;

    if (style === 'sliding') {
      return (
        <g>
          <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
          {Array.from({ length: panels }, (_, i) => (
            <g key={i}>
              <rect x={ix + i * panelW + 2} y={iy} width={panelW - 4} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} opacity={i % 2 === 0 ? 1 : 0.8} />
              {i > 0 && (
                <line x1={ix + i * panelW} y1={iy} x2={ix + i * panelW} y2={iy + ih} stroke={COLORS.frame} strokeWidth={3} />
              )}
            </g>
          ))}
          {/* Top track */}
          <rect x={x0} y={y0} width={drawW} height={4} fill={COLORS.frame} rx={1} />
          {/* Bottom track */}
          <rect x={x0} y={y0 + drawH - 4} width={drawW} height={4} fill={COLORS.frame} rx={1} />
          {/* Handle on middle panel */}
          <rect x={ix + panelW - 2} y={iy + ih * 0.45} width={4} height={ih * 0.1} rx={2} fill={COLORS.handle} />
        </g>
      );
    }

    // Fixed partition
    return (
      <g>
        <rect x={x0} y={y0} width={drawW} height={drawH} rx={2} fill={COLORS.frame} />
        {Array.from({ length: panels }, (_, i) => (
          <rect key={i} x={ix + i * panelW + 2} y={iy} width={panelW - 4} height={ih} rx={1} fill={COLORS.glass} stroke={COLORS.glassBorder} strokeWidth={1} />
        ))}
        {/* Vertical dividers */}
        {Array.from({ length: panels - 1 }, (_, i) => (
          <line key={`d${i}`} x1={ix + (i + 1) * panelW} y1={y0} x2={ix + (i + 1) * panelW} y2={y0 + drawH} stroke={COLORS.frame} strokeWidth={4} />
        ))}
      </g>
    );
  };

  const renderProduct = () => {
    if (category === 'door') return renderDoor();
    if (category === 'partition') return renderPartition();
    return renderWindow();
  };

  return (
    <div className="relative" data-testid="visual-window-builder">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="w-full h-full"
        style={{ maxHeight: '360px' }}
      >
        <defs>
          <marker id="arrowL" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
            <path d="M8,0 L0,3 L8,6" fill={COLORS.dimension} />
          </marker>
          <marker id="arrowR" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <path d="M0,0 L8,3 L0,6" fill={COLORS.dimension} />
          </marker>
          <marker id="arrowU" markerWidth="6" markerHeight="8" refX="3" refY="0" orient="auto">
            <path d="M0,8 L3,0 L6,8" fill={COLORS.dimension} />
          </marker>
          <marker id="arrowD" markerWidth="6" markerHeight="8" refX="3" refY="8" orient="auto">
            <path d="M0,0 L3,8 L6,0" fill={COLORS.dimension} />
          </marker>
          <filter id="glassShadow" x="-5%" y="-5%" width="110%" height="110%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.08" />
          </filter>
        </defs>

        {renderGrid()}
        <g filter="url(#glassShadow)">
          {renderProduct()}
        </g>
        {renderDimensions()}

        {/* Drag handles */}
        {renderHandle(x0 + drawW, y0 + drawH / 2, 'x')}
        {renderHandle(x0 + drawW / 2, y0 + drawH, 'y')}
        {renderHandle(x0 + drawW, y0 + drawH, 'both')}
      </svg>

      <div className="absolute top-3 left-3 flex gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] bg-white/90 border border-slate-200 px-2 py-1 rounded-sm text-slate-600 font-medium">
          {category}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] bg-white/90 border border-slate-200 px-2 py-1 rounded-sm text-slate-600 font-medium">
          {style}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] bg-white/90 border border-slate-200 px-2 py-1 rounded-sm text-slate-600 font-medium">
          {material}
        </span>
      </div>

      <p className="text-[10px] text-slate-400 text-center mt-1 tracking-wider uppercase">Drag blue handles to resize</p>
    </div>
  );
}
